import React, { useEffect, useRef, useState } from 'react';
import { type FuseResult } from 'fuse.js';
import { loadSkuSearchIndex, searchSkuIndex } from '../../api/sku/sku-search-api';
import { type SearchIndexEntry, type IndexState, MIN_CHARS, applyHighlight } from './PriceListShared';
import { useSkuEntitlements, can } from './PriceListEntitlements';

const VISIBLE = 60;

const BusquedaProductosTab: React.FC<{ onVerPrecios: (partNumber: string) => void }> = ({ onVerPrecios }) => {
  const entitlements = useSkuEntitlements();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<FuseResult<SearchIndexEntry>[]>([]);
  const [indexState, setIndexState] = useState<IndexState>('idle');
  const [indexError, setIndexError] = useState<string | null>(null);
  const [accumulated, setAccumulated] = useState<string[]>([]);
  const fuseRef = useRef<Awaited<ReturnType<typeof loadSkuSearchIndex>> | null>(null);

  useEffect(() => {
    setIndexState('loading');
    loadSkuSearchIndex()
      .then(idx => { fuseRef.current = idx; setIndexState('ready'); })
      .catch(() => { setIndexError('No se pudo cargar el índice de búsqueda'); setIndexState('error'); });
  }, []);

  useEffect(() => {
    if (!fuseRef.current || query.trim().length < MIN_CHARS) { setResults([]); return; }
    const t = setTimeout(() => {
      setResults(searchSkuIndex(fuseRef.current!, query));
    }, 180);
    return () => clearTimeout(t);
  }, [query]);

  const toggleAccumulate = (pn: string) => {
    setAccumulated(prev => prev.includes(pn) ? prev.filter(p => p !== pn) : [...prev, pn]);
  };

  const handleVerPreciosAccum = () => {
    if (accumulated.length === 0) return;
    onVerPrecios(accumulated.join(','));
  };

  const trimmed = query.trim();

  return (
    <div className="bp-root">

      <div className="bp-searchbar-wrap">
        <div className="bp-searchbar">
          <span className="bp-searchbar-icon" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
          </span>
          <input
            className="bp-searchbar-input"
            type="text"
            placeholder="Número de parte o descripción…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            disabled={indexState !== 'ready'}
            autoComplete="off"
            spellCheck={false}
          />
          {query && (
            <button className="bp-searchbar-clear" onClick={() => setQuery('')} aria-label="Limpiar búsqueda">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M18 6 6 18M6 6l12 12"/>
              </svg>
            </button>
          )}
        </div>

        {indexState === 'loading' && (
          <div className="bp-index-loading">
            <span className="bp-spinner" aria-hidden="true" />
            <span>Cargando índice de búsqueda…</span>
          </div>
        )}
        {indexError && <p className="pricelist-error">{indexError}</p>}
        {indexState === 'ready' && trimmed.length > 0 && trimmed.length < MIN_CHARS && (
          <p className="bp-hint">Ingrese al menos {MIN_CHARS} caracteres…</p>
        )}
        {indexState === 'ready' && trimmed.length >= MIN_CHARS && (
          <p className="bp-count">
            {results.length === 0
              ? 'Sin resultados'
              : `${results.length} resultado${results.length !== 1 ? 's' : ''}${results.length > VISIBLE ? ` — mostrando ${VISIBLE}` : ''}`}
          </p>
        )}
      </div>

      {accumulated.length > 0 && (
        <div className="bp-tray">
          <div className="bp-tray-header">
            <div className="bp-tray-label">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
              </svg>
              Seleccionados
              <span className="bp-tray-badge">{accumulated.length}</span>
            </div>
            <button className="bp-tray-clear-all" onClick={() => setAccumulated([])}>
              Limpiar todo
            </button>
          </div>
          <div className="bp-tray-chips">
            {accumulated.map(pn => (
              <span key={pn} className="bp-chip">
                <span className="bp-chip-label">{pn}</span>
                <button
                  className="bp-chip-remove"
                  onClick={() => setAccumulated(prev => prev.filter(p => p !== pn))}
                  aria-label={`Quitar ${pn}`}
                >
                  <svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                    <path d="M18 6 6 18M6 6l12 12"/>
                  </svg>
                </button>
              </span>
            ))}
          </div>
          {can.searchSku(entitlements) && (
            <button className="bp-tray-btn" onClick={handleVerPreciosAccum}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
              Ver precios de {accumulated.length} {accumulated.length === 1 ? 'producto' : 'productos'}
            </button>
          )}
        </div>
      )}

      {results.length > 0 && (
        <div className="bp-results">
          {results.slice(0, VISIBLE).map((result, i) => {
            const entry = result.item;
            const pnMatch = result.matches?.find(m => m.key === 'part_number');
            const descMatch = result.matches?.find(m => m.key === 'description');
            const isAccumulated = accumulated.includes(entry.part_number);
            return (
              <div key={entry.part_number + i} className={`bp-result-row${isAccumulated ? ' bp-result-row--selected' : ''}`}>
                <div className="bp-result-body">
                  <div className="bp-result-top">
                    <span className="bp-result-pn">
                      {applyHighlight(entry.part_number, pnMatch?.indices)}
                    </span>
                    {(entry.product_type || entry.source) && (
                      <div className="bp-result-tags">
                        {entry.product_type && <span className="bp-result-tag">{entry.product_type}</span>}
                        {entry.source && <span className="bp-result-tag bp-result-tag--source">{entry.source}</span>}
                      </div>
                    )}
                  </div>
                  {entry.description && (
                    <div className="bp-result-desc">
                      {applyHighlight(entry.description, descMatch?.indices)}
                    </div>
                  )}
                </div>
                <div className="bp-result-actions">
                  <button
                    className={`bp-accum-btn${isAccumulated ? ' bp-accum-btn--active' : ''}`}
                    onClick={() => toggleAccumulate(entry.part_number)}
                    aria-label={isAccumulated ? `Quitar ${entry.part_number} de selección` : `Agregar ${entry.part_number} a selección`}
                    title={isAccumulated ? 'Quitar de selección' : 'Agregar a selección'}
                  >
                    {isAccumulated ? (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 6 9 17l-5-5"/>
                      </svg>
                    ) : (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <path d="M12 5v14M5 12h14"/>
                      </svg>
                    )}
                  </button>
                  {can.searchSku(entitlements) && (
                    <button
                      className="bp-prices-btn"
                      onClick={() => onVerPrecios(entry.part_number)}
                      title="Ver precios para este número de parte"
                    >
                      Ver precios
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BusquedaProductosTab;
